import { Divider, Typography } from "@suraasa/placebo-ui"
import { CpatAssessmentViolations } from "api/resources/cpat/types"

const CountBadge = ({ count }: { count: number }) => {
  return (
    <div className="flex size-[60px] shrink-0 items-center justify-center rounded-full border-4 border-critical-100">
      <span className="text-2xl font-medium text-critical-500">{count}</span>
    </div>
  )
}

const VIOLATION_CONFIG: Record<string, { title: string; description: string }> =
  {
    assessmentWindowResized: {
      title: "Assessment Window Resized",
      description:
        "The assessment window size was altered during the assessment.",
    },
    cameraBlocked: {
      title: "Camera Blocked",
      description: "Camera access was blocked after the assessment started.",
    },
    appSwitched: {
      title: "Application Switched",
      description:
        "The user switched away from the assessment to another application during the session.",
    },
    tabSwitch: {
      title: "Browser Tab Switched",
      description:
        "The user navigated away from the assessment window to another browser tab.",
    },
    exitFullscreen: {
      title: "Fullscreen Exited",
      description:
        "The assessment was exited from fullscreen mode during the session.",
    },
    screenshotCaptured: {
      title: "Screenshot Captured",
      description:
        "A screenshot was attempted or captured during the assessment session.",
    },
  }

const buildViolationEvents = (violations?: Record<string, number>) => {
  if (!violations) return []

  return Object.entries(violations)
    .filter(([, count]) => count > 0)
    .map(([key, count]) => {
      const config = VIOLATION_CONFIG[key]

      if (!config) return null
      return {
        title: config.title,
        description: config.description,
        count,
      }
    })
    .filter(Boolean)
}

const DetectionSummary = ({
  id,
  data,
  className,
}: {
  id: string
  data: CpatAssessmentViolations
  className?: string
}) => {
  const { faceStats, violations } = data

  const violationEvents = buildViolationEvents(violations?.violationTypes)

  const faceIrregularityCount = faceStats.noFace + faceStats.multipleFaces

  const faceDescriptionParts = [
    faceStats.noFace > 0 && `no face detection (${faceStats.noFace})`,
    faceStats.multipleFaces > 0 &&
      `multiple face detection (${faceStats.multipleFaces})`,
  ].filter(Boolean)

  const faceDescription = `Instances of ${faceDescriptionParts.join(
    " and "
  )} were recorded during the assessment.`

  const events = [
    ...violationEvents,
    faceIrregularityCount > 0 && {
      title: "Face Detection Irregularities",
      description: faceDescription,
      count: faceIrregularityCount,
    },
  ].filter(Boolean)

  return (
    <div
      id={id}
      className={`rounded-xl border border-surface-200 bg-white ${
        className || ""
      }`}
      style={{
        boxShadow: "0px 1px 2px 0px #0F172A0D, 0px 4px 8px 0px #0F172A0D",
      }}
    >
      <Typography variant="title3" className="px-4 pt-4">
        Detection Summary
      </Typography>

      <div>
        {events.map((event, index) => {
          if (!event) return null

          return (
            <div key={`${event.title}-${index}`}>
              <div className="flex items-center gap-3 px-4 py-3.5">
                <CountBadge count={event.count} />

                <div className="flex-1">
                  <Typography variant="strong">{event.title}</Typography>

                  <Typography
                    variant="smallBody"
                    className="text-onSurface-600"
                  >
                    {event.description}
                  </Typography>
                </div>
              </div>

              {index < events.length - 1 && (
                <Divider className="bg-surface-100" />
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default DetectionSummary
